import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SearchModal.module.scss';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import getCookie from '../../../../app/helpers/getCookie';
import useIsRtl from '../../../../app/helpers/isRtl';
import RoutesData from '../../../../routes/factory/routesData';
import Text from '../../../atoms/text/Text';

export default function SearchModal({
  isOpen,
  onClose,
  routesData = RoutesData,
  className = '',
}) {
  const { t } = useTranslation();
  const isRtl = useIsRtl();
  const isEnglish = getCookie('googtrans')?.includes('/ar/en');

  const modalRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  const [query, setQuery] = useState('');

  const navigate = useNavigate();

  const handleNavigate = (item) => {
    const path = item?.route || '/';
    const state = item?.routeState;
    const replace = !!item?.shouldReplace;
    onClose?.();
    setTimeout(() => navigate(path, { state, replace }), 120);
  };

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      clearTimeout(id);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'Tab') {
        const nodes = modalRef.current?.querySelectorAll(
          'a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])'
        );
        if (!nodes?.length) return;
        const first = nodes[0],
          last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const filteredSections = useMemo(() => {
    const q = (query || '').toLowerCase();
    if (!q) return routesData;

    if (getCookie('googtrans')) return routesData;

    return routesData.reduce((acc, section) => {
      const itemTitleMatch = t(section?.title)?.toLowerCase()?.includes(q);
      const childrenTitleMatch = section?.children?.some((child) =>
        t(child?.title)?.toLowerCase()?.includes(q)
      );
      if (itemTitleMatch || childrenTitleMatch) acc.push(section);
      return acc;
    }, []);
  }, [routesData, query, t]);

  useEffect(() => {
    if (!isOpen) return;
    const isTranslated = getCookie('googtrans');
    if (isTranslated && resultsRef.current) {
      const nodes = resultsRef.current.querySelectorAll('[data-search-item]');
      const q = (query || '').toLowerCase();
      nodes.forEach((n) => {
        const match = n.innerText.toLowerCase().includes(q);
        n.style.display = match ? 'block' : 'none';
      });
    }
  }, [query, isOpen]);
  useEffect(() => {
    window.isHoveringCalendar = isOpen;
    return () => {
      window.isHoveringCalendar = false;
    };
  }, [isOpen]);
  return (
    <div
      aria-hidden={!isOpen}
      className={`${styles.overlay} ${isOpen ? styles.open : ''} ${className}`}
      onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className={`${styles.modal} ${isOpen ? styles.popIn : styles.popOut}`}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <button
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close"
        >
          <span>×</span>
        </button>
        {/* <h2 className={styles.title}>
          {isEnglish ? 'Search' : t('common.search')}
        </h2> */}

        <form className={styles.searchBar} onSubmit={(e) => e.preventDefault()}>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(DOMPurify.sanitize(e.target.value))}
            placeholder={
              isEnglish ? 'Type to search…' : ` ${t('common.search')}`
            }
            aria-label="Search input"
            style={{ textAlign: isRtl ? 'right' : 'left' }}
          />
          {/* <button type="submit">
            {isEnglish ? 'Search' : t('common.search')}
          </button> */}
        </form>

        <div ref={resultsRef} className={styles.linksGrid}>
          {filteredSections?.map((section, i) => (
            <div key={i} className={styles.col}>
              <div className={styles.colTitle}>{t(section.title)}</div>
              <ul>
                {(section.children?.length ? section.children : [section]).map(
                  (it, j) => (
                    <li key={j} data-search-item>
                      <a
                        href={it.route || '#'}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavigate(it.route ? it : section);
                        }}
                      >
                        <Text
                          title={it.title || section.title}
                          variant="text-regular-14"
                          className={styles['sec-text']}
                        />
                      </a>
                    </li>
                  )
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
